<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/employee/paySlipList" class="small d-inline-block mb-2">&larr; My payslips</router-link>
        <h1 class="h4 mb-1 font-weight-bold">
          {{ obj.no ? `Payslip ${obj.no}` : "Payslip" }}
          <CBadge
            v-if="obj.statusDescription"
            :color="hrStatusColor('payslip', obj.statusDescription)"
            class="ml-2 align-middle"
          >
            {{ hrStatusLabel('payslip', obj.statusDescription) }}
          </CBadge>
        </h1>
        <p class="text-muted mb-0">
          {{ obj.payPeriod || formatDate(obj.date) }}
        </p>
      </CCardBody>
    </CCard>

    <!-- Loading -->
    <CCard v-if="loading" class="border-0">
      <CCardBody class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3 mb-0">Loading your payslip…</p>
      </CCardBody>
    </CCard>

    <!-- Error -->
    <CCard v-else-if="loadError" class="border-0">
      <CCardBody class="text-center py-5">
        <CIcon name="cil-warning" size="3xl" class="mb-3 text-danger" />
        <h2 class="h5">Couldn't load this payslip</h2>
        <p class="text-muted mb-3">Check your connection and try again.</p>
        <CButton color="primary" @click="resetObj">
          <CIcon name="cil-reload" /> Try again
        </CButton>
      </CCardBody>
    </CCard>

    <template v-else>
      <CRow>
        <!-- Summary first on phones -->
        <CCol lg="4" class="order-lg-2">
          <CCard>
            <CCardHeader><strong>Summary</strong></CCardHeader>
            <CCardBody>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Gross pay</span>
                <span>{{ money(obj.grossAmount) }}</span>
              </div>
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">Deductions</span>
                <span>{{ money(totalDeductions) }}</span>
              </div>
              <hr />
              <div class="d-flex justify-content-between">
                <strong>Net pay</strong>
                <strong>{{ money(obj.totalAmount) }}</strong>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg="8" class="order-lg-1">
          <CCard>
            <CCardHeader><strong>Deductions and contributions</strong></CCardHeader>
            <CCardBody>
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th></th>
                      <th class="text-right">You pay</th>
                      <th class="text-right">Employer pays</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>EPF <HrGlossaryTip term="EPF" /></td>
                      <td class="text-right">{{ obj.isEnableEpf ? money(obj.employeeEpfAmount) : "Not deducted" }}</td>
                      <td class="text-right">{{ obj.isEnableEpf ? money(obj.employerEpfAmount) : "—" }}</td>
                    </tr>
                    <tr>
                      <td>SOCSO <HrGlossaryTip term="SOCSO" /></td>
                      <td class="text-right">{{ obj.isEnableSocso ? money(obj.employeeSocsoAmount) : "Not deducted" }}</td>
                      <td class="text-right">{{ obj.isEnableSocso ? money(obj.employerSocsoAmount) : "—" }}</td>
                    </tr>
                    <tr v-if="obj.isEnableEis || obj.employeeEisAmount">
                      <td>EIS <HrGlossaryTip term="EIS" /></td>
                      <td class="text-right">{{ money(obj.employeeEisAmount) }}</td>
                      <td class="text-right">{{ money(obj.employerEisAmount) }}</td>
                    </tr>
                    <tr>
                      <td>PCB <HrGlossaryTip term="PCB" /></td>
                      <td class="text-right">{{ obj.isEnableTax ? money(obj.taxAmount) : "Not deducted" }}</td>
                      <td class="text-right">—</td>
                    </tr>
                    <tr>
                      <td>Zakat <HrGlossaryTip term="Zakat" /></td>
                      <td class="text-right">{{ obj.isEnableZakat ? money(obj.zakatAmount) : "Not deducted" }}</td>
                      <td class="text-right">—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader><strong>Details</strong></CCardHeader>
            <CCardBody>
              <CRow>
                <CCol v-for="fact in facts" :key="fact.label" sm="6" class="mb-3">
                  <div class="small text-muted">{{ fact.label }}</div>
                  <div :class="{ 'text-monospace': fact.mono }">{{ fact.value || "—" }}</div>
                </CCol>
              </CRow>
              <div v-if="obj.remarks">
                <div class="small text-muted">Remarks</div>
                <p class="mb-0 remarks">{{ obj.remarks }}</p>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Printable payslip -->
      <CRow v-if="obj.id">
        <CCol sm="12">
          <WidgetsReportPayslip :payslip="computedPreviewPayslip" />
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
import PaySlipApi from "@/lib/paySlipApi";
import WidgetsReportPayslip from "../widgets/WidgetsReportPayslip";
import HrGlossaryTip from "../components/HrGlossaryTip";
import { formatDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { hrStatusMixin } from "@/utils/hrStatusBadges";

export default {
  name: "PaySlip",
  components: {
    WidgetsReportPayslip,
    HrGlossaryTip,
  },
  mixins: [hrStatusMixin],
  data: () => {
    return {
      obj: {},
      loading: true,
      loadError: false,
      api: new PaySlipApi(),
    };
  },
  mounted() {
    this.resetObj();
  },
  computed: {
    totalDeductions() {
      const o = this.obj;
      const n = (v) => parseFloat(v) || 0;
      return (
        (o.isEnableEpf ? n(o.employeeEpfAmount) : 0) +
        (o.isEnableSocso ? n(o.employeeSocsoAmount) : 0) +
        n(o.employeeEisAmount) +
        (o.isEnableTax ? n(o.taxAmount) : 0) +
        (o.isEnableZakat ? n(o.zakatAmount) : 0)
      );
    },
    facts() {
      const o = this.obj;
      return [
        { label: "Name", value: o.profile ? o.profile.fullName : o.employeeName },
        { label: "Date", value: formatDate(o.date) },
        { label: "Pay period", value: o.payPeriod },
        { label: "Reference", value: o.reference, mono: true },
        { label: "EPF number", value: o.epfNo, mono: true },
        { label: "SOCSO number", value: o.socsoNo, mono: true },
        { label: "Tax number", value: o.taxNo, mono: true },
        { label: "Bank", value: o.bankName },
        { label: "Account name", value: o.bankAccountName },
        { label: "Account number", value: o.bankAccountNo, mono: true },
      ];
    },
    // Preview computed property - ensures business object with logo is present
    computedPreviewPayslip() {
      return {
        ...this.obj,
        business: auth.getLocalBusiness(),
      };
    },
  },
  methods: {
    formatDate,
    money(value) {
      return `RM ${formatMyr(value)}`;
    },
    resetObj() {
      var self = this;
      if (!self.$route.params.id) {
        self.loading = false;
        self.loadError = true;
        return;
      }
      self.loading = true;
      self.loadError = false;
      self.api
        .get(self.$route.params.id)
        .then((response) => {
          self.obj = response.result || {};
        })
        .catch((err) => {
          console.error("Couldn't load payslip", err);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.remarks {
  white-space: pre-line;
}
</style>
